/* eslint-disable no-mixed-spaces-and-tabs */
import { mapN } from "@/utils";

export default function toBackend(simulation) {
	const { values } = simulation;
	const { useConsole } = values.spans;

	const mergeSpanValues = createMergeSpanValues(useConsole);
	const convertPointLoads = createConvertPointLoads({
		totalMainSpans: values.spans.main.length,
		useConsole,
	});

	const backendData = {
		request: {
			steelGrades: simulation.steelGrades,

			simulationType: values.type,
			location: values.location,
			inclinationDegrees: values.type !== "ROOF" ? 0 : values.angle,
			spanLengthsM: mergeSpanValues({
				main: values.spans.main,
				console: values.spans.console,
				map: (v) => v.size,
			}),
			hasRightCantilever: useConsole,
			hasLeftCantilever: useConsole,

			transverseLengthM: values.profileSpacing,
			supportWidth: values.supportWidth,
			sectionTypes: [values.profileType],
			sectionOrientation: values.profileFormat,
			profileOrientation: values.profileOrientation,

			...(() => {
				const { distributed } = values.loads.permanent;

				const min = mergeSpanValues({
					main: distributed.main,
					console: distributed.console,
					map: (v) => v.min,
				});

				const max = mergeSpanValues({
					main: distributed.main,
					console: distributed.console,
					map: (v) => v.max,
				});

				if (distributed.cfgMode.isMinMax) {
					return {
						loadPermanentDistributedMin: min,
						loadPermanentDistributedMax: max,
						loadPermanentDistributed: null,
					};
				} else {
					return {
						loadPermanentDistributedMin: null,
						loadPermanentDistributedMax: null,
						loadPermanentDistributed: min,
					};
				}
			})(),

			loadPermanentPoint: convertPointLoads(values.loads.permanent.point),

			loadLiveDistributed: mergeSpanValues({
				main: values.loads.live.distributed.main,
				console: values.loads.live.distributed.console,
			}),

			loadLivePoint: convertPointLoads(values.loads.live.point),

			loadLiveCombinationFactor: {
				psi1: values.loads.live.distributed.deltas[0],
				psi2: values.loads.live.distributed.deltas[1],
				psi3: values.loads.live.distributed.deltas[2],
			},

			loadWindPressure: {
				w1: values.wind.general.pressure.w[0],
				l1: values.wind.general.pressure.l[0],
				w2: values.wind.general.pressure.w[1],
				l2: values.wind.general.pressure.l[1],
				w3: values.wind.general.pressure.w[2],
				l3: values.wind.general.pressure.l[2],
			},
			loadWindSuction: {
				w1: values.wind.general.suction.w[0],
				l1: values.wind.general.suction.l[0],
				w2: values.wind.general.suction.w[1],
				l2: values.wind.general.suction.l[1],
				w3: values.wind.general.suction.w[2],
				l3: values.wind.general.suction.l[2],
			},
			loadWindLocalPressure: values.wind.local.use
				? {
						w1: values.wind.local.pressure.w[0],
						l1: values.wind.local.pressure.l[0],
						w2: values.wind.local.pressure.w[1],
						l2: values.wind.local.pressure.l[1],
						w3: values.wind.local.pressure.w[2],
						l3: values.wind.local.pressure.l[2],
				  }
				: {
						w1: 0,
						l1: 0,
						w2: 0,
						l2: 0,
						w3: 0,
						l3: 0,
				  },
			loadWindLocalSuction: values.wind.local.use
				? {
						w1: values.wind.local.suction.w[0],
						l1: values.wind.local.suction.l[0],
						w2: values.wind.local.suction.w[1],
						l2: values.wind.local.suction.l[1],
						w3: values.wind.local.suction.w[2],
						l3: values.wind.local.suction.l[2],
				  }
				: {
						w1: 0,
						l1: 0,
						w2: 0,
						l2: 0,
						w3: 0,
						l3: 0,
				  },
			loadWindCombinationFactor: {
				psi1: values.wind.deltas[0],
				psi2: values.wind.deltas[1],
				psi3: values.wind.deltas[2],
			},

			loadSnowRegular: mergeSpanValues({
				main: values.snow.general.main,
				console: values.snow.general.console,
			}),
			loadSnowExceptional: (() => {
				if (values.snow.exceptional.use === false) {
					return null;
				}

				return mergeSpanValues({
					main: values.snow.exceptional.main,
					console: values.snow.exceptional.console,
				});
			})(),

			loadSnowAccumulation: values.snow.snowAccumulation.use
				? {
						w1: values.snow.snowAccumulation.s[0],
						l1: values.snow.snowAccumulation.l[0],
						w2: values.snow.snowAccumulation.s[1],
						l2: values.snow.snowAccumulation.l[1],
						w3: values.snow.snowAccumulation.s[2],
						l3: values.snow.snowAccumulation.l[2],
				  }
				: {
						w1: 0,
						l1: 0,
						w2: 0,
						l2: 0,
						w3: 0,
						l3: 0,
				  },
			loadSnowCombinationFactor: {
				psi1: values.snow.deltas[0],
				psi2: values.snow.deltas[1],
				psi3: values.snow.deltas[2],
			},
			deflectionLimit: {
				limit: values.deformation,
				combination: values.combination,
			},
			axialLoad: {
				valueKn: values.axialLoads.value,
				direction: values.axialLoads.direction,
				type: values.axialLoads.type,
			},
			spanRestraints: {
				upper: mergeSpanValues({
					main: values.locks.upperFibre.lateralLocks.main,
					console: values.locks.upperFibre.lateralLocks.console,
				}),
				lower: mergeSpanValues({
					main: values.locks.lowerFibre.lateralLocks.main,
					console: values.locks.lowerFibre.lateralLocks.console,
				}),
			},
			claddingProperties: values.claddingProperties.use
				? {
						coveringInertia: values.claddingProperties.coveringInertia,
						fixationsPerMeter: values.claddingProperties.fixationsPerMeter,
				  }
				: {
						coveringInertia: 0,
						fixationsPerMeter: 0,
				  },

			metadata: {
				language: "PT",
				description: "",
				projectName: simulation.project,
				calculationDate: simulation.date,
			},
		},
		chosenResultId: simulation.chosenSolution,
		name: simulation.name,
		description: "",
	};

	return backendData;
}

function createMergeSpanValues(useConsole) {
	return function mergeSpanValues({ main, console, map = (v) => v }) {
		return [
			useConsole ? map(console[0]) : null,
			...main.map(map),
			useConsole ? map(console[1]) : null,
		].filter((v) => v !== null);
	};
}

function createConvertPointLoads({ totalMainSpans, useConsole }) {
	return function convertPointLoads(loads) {
		const loadsBySpan = loads.flatMap(({ targetSpans, loads }) => {
			return targetSpans.map((spanId) => {
				const [spanType, spanIdx] = spanId.split(":");

				return {
					spanType,
					spanIdx: +spanIdx,
					loads: {
						p1: loads[0],
						p2: loads[1],
						l1: loads[2],
						l2: loads[3],
					},
				};
			});
		});

		let result = [];

		const mainLoads = mapN(totalMainSpans, (i) => {
			const match = loadsBySpan.find(({ spanType, spanIdx }) => {
				return spanType === "span" && spanIdx === i;
			});

			if (match) {
				return match.loads;
			} else {
				return {
					p1: 0,
					p2: 0,
					l1: 0,
					l2: 0,
				};
			}
		});

		result = [...mainLoads];

		if (useConsole) {
			const consoleLoads = mapN(totalMainSpans + 1, (i) => {
				const match = loadsBySpan.find(({ spanType, spanIdx }) => {
					return spanType === "console" && spanIdx === i;
				});

				if (match) {
					return match.loads;
				} else {
					return {
						p1: 0,
						p2: 0,
						l1: 0,
						l2: 0,
					};
				}
			});

			result = [consoleLoads[0], ...result, consoleLoads[1]];
		}

		return result;
	};
}
